import { html, css, when, ref } from '@genesislcap/web-core';
import { MuniDeals } from './deals';
import { customEvent } from '@genesislcap/foundation-events';
import { DealDetail } from './deal-detail/deal-detail';
import { ExecutionContext, repeat } from '@genesislcap/web-core';
import { FilterPill } from './deals.types';
import { DealSeriesInformation } from './deal-series/deal-series-information/deal-series-information';
import { HIGHLIGHT_RECONFIRM_BLUE } from '../../styles';

DealDetail;
DealSeriesInformation;

const slottedStyles = css`
  .group-border {
    border: 1px solid white;
  }

  :host .ag-floating-filter-button {
    margin-left: 5px !important;
  }

  .ag-set-filter-list,
  .ag-filter-no-matches {
    height: calc(var(--ag-list-item-height) * 10) !important;
  }

  @keyframes reconfirm-blink {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.85;
    }
  }

  /* Blue gradient: lighter at both ends, darker in the middle (rgb 74,158,255) */
  .deal-row-reconfirm-window,
  .deal-row-reconfirm-window.ag-row-hover {
    border-left: 3px solid ${HIGHLIGHT_RECONFIRM_BLUE};
    background: linear-gradient(
      to right,
      rgb(74, 158, 255, 0.1) 0%,
      rgb(74, 158, 255, 0.32) 50%,
      rgb(74, 158, 255, 0.1) 100%
    ) !important;
    animation: reconfirm-blink 1.5s ease-in-out infinite;
  }

  .deal-row-reconfirm-window .ag-cell,
  .deal-row-reconfirm-window.ag-row-hover .ag-cell {
    background: transparent !important;
    animation: reconfirm-blink 1.5s ease-in-out infinite;
  }
`;

export const dealsTemplate = html<MuniDeals>`
  <div class="tab-container">
    <div class="header">
      <div class="header-group">
        <div class="label">Date range</div>
        <div class="header-group-date" ${ref('dateCalendar')}>
          <div class="header-group-date-toggle" @click="${(x) => x.toggleCalendar()}">
            <rapid-icon name="calendar" size="lg"></rapid-icon>
            <span>
              ${(x) => (x.forwardCalendar ? 'Forward Calendar' : `Week of ${x.dealsDate}`)}
            </span>
          </div>
          ${when(
            (x) => x.dealsDateCalendar,
            html<MuniDeals>`
              <div class="header-group-date-calendar">
                <rapid-date-picker
                  ${ref('weekPicker')}
                  :disabledDaysOfWeek="${() => [1, 3, 4, 5, 6, 7]}"
                  :value="${(x) => x.dealsDate}"
                  @value-changed="${(x, ctx) => x.setDealsDate(customEvent(ctx))}"
                  hide-weekends
                  inline-calendar
                >
                  <div slot="bottom">
                    <div class="header-group-date-calendar-actions">
                      <span
                        @click="${(x) => x.goToHome()}"
                        class="header-group-date-calendar-actions-item"
                      >
                        This week
                      </span>
                      <span
                        @click="${(x) => x.toggleForwardCalendar()}"
                        class="header-group-date-calendar-actions-item"
                      >
                        Forward Calendar
                      </span>
                    </div>
                  </div>
                </rapid-date-picker>
              </div>
            `,
          )}
        </div>
        <span class="control-with-tooltip">
          <rapid-button
            id="muni-deals-nav-prev-week"
            class="control-icon"
            @click="${(x) => x.goToPreviousWeek()}"
          >
            <img alt="Previous week" src="/deal-icons/chevron-left.svg" />
          </rapid-button>
          <rapid-tooltip anchor="muni-deals-nav-prev-week" delay="2000" position="bottom">Previous week</rapid-tooltip>
        </span>
        <span class="control-with-tooltip">
          <rapid-button
            id="muni-deals-nav-home"
            class="control-icon"
            @click="${(x) => x.goToHome()}"
          >
            <img alt="Home" src="/deal-icons/home.svg" />
          </rapid-button>
          <rapid-tooltip anchor="muni-deals-nav-home" delay="2000" position="bottom">Home</rapid-tooltip>
        </span>
        <span class="control-with-tooltip">
          <rapid-button
            id="muni-deals-nav-next-week"
            class="control-icon"
            ?disabled="${(x) => x.forwardCalendar}"
            @click="${(x) => x.goToNextWeek()}"
          >
            <img alt="Next week" src="/deal-icons/chevron-right.svg" />
          </rapid-button>
          <rapid-tooltip anchor="muni-deals-nav-next-week" delay="2000" position="bottom">Next week</rapid-tooltip>
        </span>
        <span class="control-with-tooltip">
          <rapid-button
            id="muni-deals-nav-forward-week"
            class="control-icon"
            ?disabled="${(x) => x.forwardCalendar}"
            @click="${(x) => x.toggleForwardCalendar()}"
          >
            <img alt="Forward week" src="/deal-icons/chevron-right-double.svg" />
          </rapid-button>
          <rapid-tooltip anchor="muni-deals-nav-forward-week" delay="2000" position="bottom">Forward week</rapid-tooltip>
        </span>
      </div>
      <rapid-divider orientation="vertical"></rapid-divider>
      <div class="header-group">
        <div class="label">Filters:</div>
        <div class="active-filters">
          ${repeat(
            (x) => x.filterPills,
            html<FilterPill>`
              <div
                class="filter-pill"
                @click="${(x, ctx: ExecutionContext<MuniDeals>) => ctx.parent.removeFilter(x.key)}"
              >
                <div class="filter-pill-label">${(x) => x.label}</div>
                <rapid-icon size="lg" name="xmark"></rapid-icon>
              </div>
            `,
          )}
        </div>
      </div>
      <div class="header-group header-group-right">
        <rapid-button @click="${(x) => x.clearAllFilters()}">
          <rapid-icon slot="start" name="filter-circle-xmark" size="xl"></rapid-icon>
          Clear All Filters
        </rapid-button>
      </div>
    </div>
    <div class="grid-container">
      <rapid-grid-pro
        @gridReady="${(x, ctx) => x.handleGridReady(customEvent(ctx))}"
        @rowClicked="${(x, ctx) => x.handleRowClick(customEvent(ctx))}"
        :gridOptions="${(x) => x.gridOptions}"
      >
        <slotted-styles :styles="${() => slottedStyles}"></slotted-styles>
        <grid-pro-genesis-datasource
          ${ref('dealsDatasource')}
          fields="DEAL_ID DEAL_TYPE DEAL_STATUS EXPECTED_PRICING_DATE DEAL_SIZE DEAL_DESCRIPTION STATE TAX_STATUS DEAL_MOODYS_RATING DEAL_FITCH_RATING DEAL_SP_RATING DEAL_KROLL_RATING SYNDICATE SHORT_NAME DEAL_TRADE_DATE DEAL_SECTOR ORDER_PERIOD_BEGIN_DATE ORDER_PERIOD_END_DATE COUNTERPARTY_ELIGIBILITY SUBMITTED_QTY DRAFT_QTY ORDER_DISPLAY_QTY NON_SUCCESS_ORDER_COUNT"
          max-rows="1000"
          max-view="3000"
          moving-view="true"
          keep-col-defs-on-clear-row-data
          :criteria=${(x) => x.criteria}
          resource-name="ALL_MUNIS_DEALS_VIEW"
        ></grid-pro-genesis-datasource>
      </rapid-grid-pro>
    </div>
  </div>
`;
